import { defineStore } from "pinia";
import { ref } from "vue";
import goalTrackingService from "@/services/hrm/goalTrackingService";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const useGoalTrackingStore = defineStore("goalTracking", () => {
  const items = ref([]);
  const pagination = ref({ total: 0, per_page: 25, current_page: 1 });
  const loading = ref(false);
  const error = ref(null);

  const filters = ref({
    search: "",
    status: "",
    subject: "",
    goal_type: "",
    date_from: "",          // ⬅️ add
    date_to: "",            // ⬅️ add
    per_page: 25,
    page: 1,
  });

  async function fetchAll(extra = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_PERFORMANCE))) {
      items.value = [];
      pagination.value = { total: 0, per_page: 25, current_page: 1 };
      return;
    }

    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      const res = await goalTrackingService.getAll(params);
      const data = res.data;

      // Laravel paginator shape
      if (data?.data && (data?.meta || data?.total !== undefined)) {
        items.value = data.data;
        pagination.value = {
          total: data.meta?.total ?? data.total ?? data.data.length,
          per_page: data.meta?.per_page ?? data.per_page ?? params.per_page ?? 25,
          current_page: data.meta?.current_page ?? data.current_page ?? 1,
        };
      } else {
        // array fallback
        items.value = Array.isArray(data) ? data : [];
        pagination.value = {
          total: items.value.length,
          per_page: params.per_page ?? 25,
          current_page: 1,
        };
      }
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch goal tracking";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function create(payload) {
    loading.value = true;
    try {
      await goalTrackingService.create(payload);
      toast("Goal tracking created");
      await fetchAll({ page: 1 });
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to create";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    try {
      await goalTrackingService.update(id, payload);
      toast("Goal tracking updated");
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to update";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true;
    try {
      await goalTrackingService.delete(id);
      toast("Goal tracking deleted");
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    loading.value = true;
    try {
      await goalTrackingService.bulkDelete(ids);
      toast("Selected goal trackings deleted");
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to bulk delete";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    items, pagination, loading, error, filters,
    fetchAll, create, update, remove, bulkDelete
  };
});
